import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, Skeleton, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import axiosInstance from "../AxiosInstance";
import { dashboardWidgetCardSx } from "./dashboard/dashboardDisplay";

interface CustomerOrderingDashboardRow {
    customerId: number;
    customerName: string;
    totalOrdersYtd: number;
    lastOrderDateUtc: string;
    orderRegularityDays: number;
    daysSinceLastOrder: number;
    daysOver: number;
    isOverdue: boolean;
}

interface CustomerOrderingDashboardData {
    forecastYearStartDateUtc: string;
    forecastYearEndDateUtc: string;
    rows: CustomerOrderingDashboardRow[];
}

const formatDate = (value?: string): string => {
    if (!value) return "";
    return new Date(value).toLocaleDateString("en-GB");
};

const CustomerOrderingDashboardPanel: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CustomerOrderingDashboardData | null>(null);

    useEffect(() => {
        let cancelled = false;

        const load = async (): Promise<void> => {
            try {
                setLoading(true);
                const response = await axiosInstance.get<CustomerOrderingDashboardData>("/dashboard/reorder-status");
                if (!cancelled) {
                    setData(response.data);
                    setError(null);
                }
            } catch (err) {
                console.error("Error loading customer ordering dashboard data:", err);
                if (!cancelled) {
                    setError("Could not load customer reorder data.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const columns: GridColDef<CustomerOrderingDashboardRow>[] = useMemo(() => ([
        {
            field: "customerName",
            headerName: "Customer",
            flex: 1.2,
            minWidth: 220,
        },
        {
            field: "lastOrderDateUtc",
            headerName: "Last Order",
            type: "date",
            minWidth: 170,
            valueGetter: (_, row) => {
                if (!row.lastOrderDateUtc) return null;
                return new Date(row.lastOrderDateUtc);
            },
            valueFormatter: (value) => {
                const dateValue = value as Date | null;
                if (!dateValue || Number.isNaN(dateValue.getTime())) return "";
                return formatDate(dateValue.toISOString());
            },
        },
        {
            field: "totalOrdersYtd",
            headerName: "Total Orders",
            type: "number",
            minWidth: 170,
            headerAlign: "right",
            align: "right",
        },
        {
            field: "orderRegularityDays",
            headerName: "Interval Days",
            type: "number",
            minWidth: 170,
            headerAlign: "right",
            align: "right",
        },
        {
            field: "daysOver",
            headerName: "Days Overdue",
            type: "number",
            minWidth: 190,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: params.row.isOverdue ? 600 : 400,
                        color: params.row.isOverdue ? "error.main" : "text.primary",
                    }}
                >
                    {params.value}
                </Typography>
            ),
        },
    ]), []);

    const subtitle = useMemo(() => {
        if (!data) return "";
        return `YTD from ${formatDate(data.forecastYearStartDateUtc)}`;
    }, [data]);

    return (
        <Card variant="outlined" sx={dashboardWidgetCardSx}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
                    Reorder Status
                </Typography>
                {subtitle ? (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>
            {error ? (
                <Alert severity="error">{error}</Alert>
            ) : loading ? (
                <Skeleton variant="rectangular" width="100%" height={420} sx={{ borderRadius: 1 }} />
            ) : (
                <Box sx={{ height: 420, width: "100%" }}>
                    <DataGridPro
                        rows={data?.rows || []}
                        columns={columns}
                        getRowId={(row) => row.customerId}
                        disableRowSelectionOnClick
                        density="compact"
                        pagination
                        pageSizeOptions={[15, 25, 50]}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "daysOver", sort: "desc" }],
                            },
                            pagination: {
                                paginationModel: { pageSize: 15, page: 0 },
                            },
                        }}
                        sx={{
                            border: "none",
                            "& .MuiDataGrid-columnHeaderTitle": {
                                whiteSpace: "nowrap !important",
                            },
                            "& .MuiDataGrid-columnSeparator": {
                                color: "divider",
                            },
                        }}
                    />
                </Box>
            )}
        </Card>
    );
};

export default CustomerOrderingDashboardPanel;
